import React, { createContext, useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import useAuth from "@app/common/auth/useAuth";
import {
  UNKNOWN_PLAN,
  UNKNOWN_SUBSCRIPTION,
} from "@app/common/constants/billing";
import useBillingPlanSubscriptionContext from "@app/common/hooks/useBillingPlanSubscriptionContext";
import usePrevious from "@app/common/hooks/usePrevious";
import type {
  BillingCurrentPlan,
  BillingScheduledPlan,
  BillingSubscription,
  UserBillingAccount,
} from "@app/common/types";
import {
  getRemainingTrialDays,
  planIsUnknown,
} from "@app/common/utils/billingUtils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import { isFreePlan } from "../account/Billing/utils";

interface BillingContextProviderProps {
  children: React.ReactNode;
}

interface BillingContext {
  subscription: BillingSubscription;
  currentPlan: BillingCurrentPlan;
  billingAccount: UserBillingAccount | null;
  isEligibleForLegacyPricing: boolean;
  isTrial: boolean;
  licenses: {
    /** Remaining number of licenses available for purchase.
     * Only applicable to subscriptions with a seat maximum (i.e., Zoom plan) */
    numAvailableForPurchase?: number;
    /** Number of active licenses that are currently claimed by a workspace member/invite */
    numClaimed: number;
    /** Number of active licenses that are not currently claimed by a workspace member/invite */
    numUnclaimed: number;
    /** Total number of active licenses. This is the number that is exposed to users, and so
     * will reflect any scheduled license removals. */
    numActive: number;
    /** Total number of licenses that were purchased for the current billing period
     * but are scheduled for removal. These are licenses the user could "re-purchase"
     * during the current billing period at no additional cost */
    numInactive: number;
    /** Total number of licenses that have already been paid for (does not account for scheduled license removals) */
    numPaid: number;
  };
  plansInitialLoading: boolean;
  planLoadFailed: boolean;
  scheduledPlan?: BillingScheduledPlan;
  subscriptionIsError: boolean;
  trialDaysRemaining: number;
  uploadCredits: {
    monthlyBalance: number;
    purchasedBalance: number;
  };
}

const DEFAULT_BILLING_CONTEXT: BillingContext = {
  subscription: UNKNOWN_SUBSCRIPTION,
  currentPlan: UNKNOWN_PLAN,
  billingAccount: null,
  isEligibleForLegacyPricing: false,
  isTrial: false,
  licenses: {
    numAvailableForPurchase: 0,
    numClaimed: 1,
    numUnclaimed: 0,
    numActive: 1,
    numInactive: 0,
    numPaid: 1,
  },
  plansInitialLoading: true,
  planLoadFailed: false,
  subscriptionIsError: false,
  trialDaysRemaining: 0,
  uploadCredits: {
    monthlyBalance: 0,
    purchasedBalance: 0,
  },
};

export const BillingContext = createContext<BillingContext>(
  DEFAULT_BILLING_CONTEXT
);

export const BillingContextProvider = ({
  children,
}: BillingContextProviderProps) => {
  const queryClient = useQueryClient();

  const { user, fetchAndSetUser } = useAuth();
  const { inWorkspace } = useWorkspace();

  const { hasPaymentMethod } = usePaymentMethod();
  const {
    currentPlan,
    isLoading: isSubscriptionLoading,
    isError: subscriptionIsError,
    scheduledPlan,
    subscription,
  } = useBillingPlanSubscriptionContext();

  const isPlanUnknown = planIsUnknown(currentPlan.type);
  const currentSubscriptionPlan = isPlanUnknown ? null : currentPlan.type;
  const previousSubscriptionPlan = usePrevious(
    isPlanUnknown ? null : currentPlan.type
  );

  useEffect(() => {
    if (previousSubscriptionPlan == null || currentSubscriptionPlan == null) {
      return;
    }

    if (currentSubscriptionPlan !== previousSubscriptionPlan) {
      // re-fetch user access permissions and billing account when plan changes
      fetchAndSetUser();

      if (
        inWorkspace &&
        isFreePlan(previousSubscriptionPlan, true) !==
          isFreePlan(currentSubscriptionPlan, true)
      ) {
        // if shifting onto or from Free plan, refetch the workspace to get updated status
        queryClient.invalidateQueries({ queryKey: ["workspace"] });
      }
    }
  }, [inWorkspace, previousSubscriptionPlan, currentSubscriptionPlan]);

  const billingAccount = user?.billingAccount ?? null;

  // true when subscription is in trial period and has not been upgraded.
  // false after trial period or when in trial period, but subscription
  // has already been upgraded (CC info entered) and is not set to cancel
  // at the end of the trial period
  const isTrial =
    subscription.inTrialPeriod &&
    (!hasPaymentMethod || Boolean(subscription.cancelAtPeriodEnd));

  const trialEndDate =
    currentPlan.type === "zoom_bundle_trial"
      ? subscription.currentPeriodEnd
      : billingAccount?.freeTrialExpirationDate;

  const trialDaysRemaining = getRemainingTrialDays(trialEndDate);

  const isEligibleForLegacyPricing =
    billingAccount != null && billingAccount.isEligibleForLegacyPricing;

  const numActiveLicenses =
    subscription.scheduledUpdate?.quantity ?? subscription.quantity;
  const numClaimedLicenses = billingAccount?.billableSeats ?? 1;

  const licenses = {
    numAvailableForPurchase: currentPlan.seatMaximum
      ? Math.max(currentPlan.seatMaximum - numActiveLicenses, 0)
      : undefined,
    numClaimed: numClaimedLicenses,
    numUnclaimed: Math.max(0, numActiveLicenses - numClaimedLicenses),
    numActive: numActiveLicenses,
    numInactive: Math.max(0, subscription.quantity - numActiveLicenses),
    numPaid: subscription.quantity,
  };

  const uploadCredits = {
    monthlyBalance: subscription.monthlyUploadCreditBalance ?? 0,
    purchasedBalance: subscription.purchasedUploadCreditBalance ?? 0,
  };

  const planLoadFailed = subscriptionIsError && isPlanUnknown;

  const context = useMemo(
    () => ({
      currentPlan,
      subscription,
      licenses,
      billingAccount,
      isEligibleForLegacyPricing,
      isTrial,
      plansInitialLoading: isSubscriptionLoading,
      planLoadFailed,
      scheduledPlan,
      subscriptionIsError,
      trialDaysRemaining,
      uploadCredits,
    }),
    [
      billingAccount,
      currentPlan,
      scheduledPlan,
      subscription,
      isEligibleForLegacyPricing,
      planLoadFailed,
      isTrial,
      isSubscriptionLoading,
      licenses,
      subscriptionIsError,
      trialDaysRemaining,
    ]
  );

  return (
    <BillingContext.Provider value={context}>
      {children}
    </BillingContext.Provider>
  );
};
